import {
  getEnabledElement,
  cache,
  utilities as csUtils,
  Enums,
  eventTarget,
  BaseVolumeViewport,
} from '@cornerstonejs/core';
import type { Types } from '@cornerstonejs/core';

import { BaseTool } from '../base';
import SegmentationRepresentations from '../../enums/SegmentationRepresentations';
import type vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import { getActiveSegmentation } from '../../stateManagement/segmentation/getActiveSegmentation';
import { getLockedSegmentIndices } from '../../stateManagement/segmentation/segmentLocking';

import { getSegmentation } from '../../stateManagement/segmentation/getSegmentation';
import { getCurrentLabelmapImageIdForViewport } from '../../stateManagement/segmentation/getCurrentLabelmapImageIdForViewport';
import { getSegmentIndexColor } from '../../stateManagement/segmentation/config/segmentationColor';
import { getActiveSegmentIndex } from '../../stateManagement/segmentation/getActiveSegmentIndex';
import { StrategyCallbacks } from '../../enums';
import * as LabelmapMemo from '../../utilities/segmentation/createLabelmapMemo';
import {
  getAllAnnotations,
  removeAnnotation,
} from '../../stateManagement/annotation/annotationState';
import { filterAnnotationsForDisplay } from '../../utilities/planar';
import { iterateContourFillVoxels } from './strategies/utils/contourVoxelSlab';
import { triggerSegmentationDataModified } from '../../stateManagement/segmentation/triggerSegmentationEvents';
import { fillInsideCircle } from './strategies';
import type { LabelmapToolOperationData } from '../../types/LabelmapToolOperationData';
import getViewportLabelmapRenderMode from '../../stateManagement/segmentation/helpers/getViewportLabelmapRenderMode';
import {
  getOrCreateLabelmapVolume,
  resolveLabelmapForSegment,
} from '../../stateManagement/segmentation/helpers/labelmapSegmentationState';
import getViewportICamera from '../../utilities/getViewportICamera';
import type { Annotations } from '../../types';
import type { ContourSegmentationAnnotation } from '../../types/ContourSegmentationAnnotation';
import { getHiddenSegmentIndices } from '../../stateManagement/segmentation/config/segmentationVisibility';
import { removeContourSegmentationAnnotation } from '../../utilities/contourSegmentation/removeContourSegmentationAnnotation';
import triggerAnnotationRenderForViewportIds from '../../utilities/triggerAnnotationRenderForViewportIds';
import { resetElementCursor } from '../../cursors/elementCursor';

const { asUnitNormal } = csUtils.voxelSlab;

/**
 * A type for preview data/information, used to setup previews on hover, or
 * maintain the preview information.
 */
export type PreviewData = {
  /**
   *  The preview data returned from the strategy
   */
  preview: unknown;
  /** A timer id to allow cancelling the timer */
  timer?: number;
  /** The start time for the timer, to allow showing preview after a given length of time */
  timerStart: number;
  /**
   * The starting point where the use clicked down on, used to cancel preview
   * on drag, but preserve it if the user moves the mouse tiny amounts accidentally.
   */
  startPoint: Types.Point2;
  element: HTMLDivElement;
  /**
   * Record if this is a drag preview, that is, a preview which is being extended
   * by the user dragging to view more area.
   */
  isDrag: boolean;
};

type EditDataReturnType =
  | {
      volumeId: string;
      referencedVolumeId: string;
      segmentsLocked: number[];
    }
  | {
      imageId: string;
      segmentsLocked: number[];
      override?: {
        voxelManager:
          | Types.IVoxelManager<number>
          | Types.IVoxelManager<Types.RGB>;
        imageData: vtkImageData;
      };
    }
  | null;

type ModifiedLabelmapToolOperationData = Omit<
  LabelmapToolOperationData,
  'voxelManager' | 'override'
> & {
  voxelManager?: Types.IVoxelManager<number> | Types.IVoxelManager<Types.RGB>;
  override?: {
    voxelManager: Types.IVoxelManager<number> | Types.IVoxelManager<Types.RGB>;
    imageData: vtkImageData;
  };
};

/**
 * Labelmap tool containing shared functionality for labelmap tools.
 */
export default class LabelmapBaseTool extends BaseTool {
  protected _editData: {
    override: {
      voxelManager: Types.IVoxelManager<number>;
      imageData: vtkImageData;
    };
    segmentsLocked: number[]; //
    imageId?: string; // stack labelmap
    imageIds?: string[]; // stack labelmap
    volumeId?: string; // volume labelmap
    referencedVolumeId?: string;
  } | null;

  protected centerSegmentIndexInfo: {
    segmentIndex: number;
    hasSegmentIndex: boolean;
    hasPreviewIndex: boolean;
    changedIndices: number[];
  };

  protected _hoverData?: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    brushCursor: any;
    segmentationId: string;
    segmentIndex: number;
    segmentColor: [number, number, number, number];
    viewportIdsToRender: string[];
    centerCanvas?: Array<number>;
    viewport: Types.IViewport;
  };

  public static previewData?: PreviewData = {
    preview: null,
    element: null,
    timerStart: 0,
    timer: null,
    startPoint: [NaN, NaN],
    isDrag: false,
  };

  /**
   * Whether a draw loop is currently in progress. Declared here so the shared
   * touch-cancellation path can reason about it; the drawing tools narrow it.
   */
  protected isDrawing?: boolean;

  /**
   * In-progress draw state. Declared here only with the members the shared
   * touch-cancellation path needs; the drawing tools narrow it to their own
   * (richer) shape.
   */
  protected editData?: { viewportIdsToRender?: string[] } | null;

  protected memoMap: Map<string, LabelmapMemo.LabelmapMemo>;
  protected acceptedMemoIds: Map<
    string,
    { element: HTMLDivElement; segmentIndex: number }
  >;
  protected memo: LabelmapMemo.LabelmapMemo;

  constructor(toolProps, defaultToolProps) {
    super(toolProps, defaultToolProps);
    this.memoMap = new Map();
    this.acceptedMemoIds = new Map();
    this.centerSegmentIndexInfo = {
      segmentIndex: null,
      hasSegmentIndex: false,
      hasPreviewIndex: false,
      changedIndices: [],
    };
  }

  /**
   * Cancels an in-progress touch draw when the gesture turns out to be
   * multi-finger (pinch zoom, multi-finger scroll). The rubber band of the
   * scissors tools lives only in `editData` and is never written to the
   * annotation state, so tearing the draw loop down and re-rendering makes it
   * disappear without applying anything to the labelmap.
   *
   * Tools whose stroke has already written voxels (BrushTool) need to roll
   * those writes back as well and therefore override this.
   */
  protected _cancelTouchDraw(element: HTMLDivElement): void {
    if (!this.isDrawing) {
      return;
    }

    const viewportIdsToRender = this.editData?.viewportIdsToRender;

    // Declared on the subclasses (BrushTool declares it `private`, so it
    // cannot be widened to `protected` on this class).
    (
      this as unknown as { _deactivateDraw: (element: HTMLDivElement) => void }
    )._deactivateDraw(element);

    resetElementCursor(element);
    this.editData = null;
    this.isDrawing = false;

    if (viewportIdsToRender) {
      triggerAnnotationRenderForViewportIds(viewportIdsToRender);
    }
  }

  protected _historyRedoHandler(evt) {
    const { id, operationType } = evt.detail;

    // Skip if not a labelmap operation
    if (operationType !== 'labelmap') {
      return;
    }

    if (this.acceptedMemoIds.has(id)) {
      // Note: this is very important to null here, since the undo might happen while
      // the viewport is not active OR through some UI, so the cursor might not be
      // on the element so we need to null out the hover data so that it get
      // recalculated again based on the current element (that we stored previously)
      this._hoverData = null;

      const memoData = this.acceptedMemoIds.get(id);

      const element = memoData?.element;
      const operationData = this.getOperationData(element);
      if (!operationData) {
        return;
      }

      operationData.segmentIndex = memoData?.segmentIndex;

      if (element) {
        this.applyActiveStrategyCallback(
          getEnabledElement(element),
          operationData,
          StrategyCallbacks.AcceptPreview
        );
      }
    }

    // Mark the preview as a drag to prevent additional processing
    this._previewData.isDrag = true;
  }

  // Gets a shared preview data
  protected get _previewData() {
    return LabelmapBaseTool.previewData;
  }

  /**
   * Stores the result of a strategy as the shared preview, but only when the
   * strategy actually set a preview up.
   *
   * `BrushStrategy.fill` returns its initialized data for every stroke, with a
   * preview or without one, so the raw result cannot go into `previewData.preview`
   * directly. `modified` is the field that says a preview segment index went onto
   * the labelmap: the `preview` composition sets `modified` to true only when both
   * `previewSegmentIndex` and `segmentIndex` are present. `addPreview` uses the
   * same test.
   *
   * Keeping `previewData.preview` truthful is what lets every labelmap tool share
   * one preview: any tool can then reject the preview that any other tool created.
   */
  protected _setPreview(results) {
    this._previewData.preview = results?.modified ? results : null;
  }

  /**
   * Checks if the tool has a preview data associated.
   * @returns True if the tool has preview data, false otherwise.
   */
  public hasPreviewData() {
    return !!this._previewData.preview;
  }

  /**
   * Checks if the tool should resolve preview requests.
   * This is used to determine if the tool is in a state where it can handle
   * preview requests.
   * @returns True if the tool should resolve preview requests, false otherwise.
   */
  public shouldResolvePreviewRequests() {
    return (
      (this.mode === 'Active' || this.mode === 'Enabled') &&
      this.hasPreviewData()
    );
  }

  /**
   * Creates a labelmap memo instance, which stores the changes made to the
   * labelmap rather than the initial state.
   */
  public createMemo(segmentationId: string, segmentationVoxelManager) {
    const voxelManagerId = segmentationVoxelManager.id;

    if (
      this.memo &&
      this.memo.segmentationVoxelManager === segmentationVoxelManager
    ) {
      return this.memo;
    }

    let memo = this.memoMap.get(voxelManagerId);

    if (!memo) {
      memo = LabelmapMemo.createLabelmapMemo(
        segmentationId,
        segmentationVoxelManager
      );
      this.memoMap.set(voxelManagerId, memo);
    } else {
      // If the memo was previously committed, we need a fresh one
      if (memo.redoVoxelManager) {
        memo = LabelmapMemo.createLabelmapMemo(
          segmentationId,
          segmentationVoxelManager
        );
        this.memoMap.set(voxelManagerId, memo);
      }
    }

    this.memo = memo;
    return memo;
  }

  protected createEditData(element): EditDataReturnType {
    const enabledElement = getEnabledElement(element);
    const { viewport } = enabledElement;

    const activeSegmentation = getActiveSegmentation(viewport.id);
    if (!activeSegmentation) {
      return null;
    }

    const { segmentationId } = activeSegmentation;

    const segmentsLocked = getLockedSegmentIndices(segmentationId);

    const { representationData } = getSegmentation(segmentationId);

    const editData = this.getEditData({
      viewport,
      representationData,
      segmentsLocked,
      segmentationId,
    });

    return editData;
  }

  protected getEditData({
    viewport,
    representationData,
    segmentsLocked,
    segmentationId,
  }): EditDataReturnType {
    const viewportRenderMode = getViewportLabelmapRenderMode(viewport);
    const activeSegmentIndex = getActiveSegmentIndex(segmentationId);
    const segmentation = getSegmentation(segmentationId);
    const layerForEdit = activeSegmentIndex
      ? resolveLabelmapForSegment(segmentation, activeSegmentIndex)
      : undefined;

    if (
      viewportRenderMode === 'volume' ||
      viewport instanceof BaseVolumeViewport
    ) {
      const segmentationVolume = layerForEdit
        ? getOrCreateLabelmapVolume(layerForEdit)
        : undefined;
      const volumeId = layerForEdit?.volumeId ?? segmentationVolume?.volumeId;

      if (!segmentationVolume || !volumeId) {
        return;
      }

      const actors = viewport.getActors();

      // we used to take the first actor here but we should take the one that is
      // probably the same size as the segmentation volume
      const volumes = actors
        .filter((actorEntry) => actorEntry.referencedId)
        .map((actorEntry) => cache.getVolume(actorEntry.referencedId))
        .filter((volume): volume is Types.IImageVolume => !!volume);

      const referencedVolumeIdToThreshold =
        volumes.find((volume) =>
          csUtils.isEqual(volume.dimensions, segmentationVolume.dimensions)
        )?.volumeId || volumes[0]?.volumeId;

      return {
        volumeId,
        referencedVolumeId:
          this.configuration.threshold?.volumeId ??
          layerForEdit?.referencedVolumeId ??
          segmentationVolume.referencedVolumeId ??
          referencedVolumeIdToThreshold,
        segmentsLocked,
      };
    }

    if (viewportRenderMode === 'image') {
      const segmentationImageId = getCurrentLabelmapImageIdForViewport(
        viewport.id,
        segmentationId
      );

      if (!segmentationImageId) {
        // if there is no stack segmentation slice for the current image
        // we should not allow the user to perform any operation
        return;
      }

      return {
        imageId: segmentationImageId,
        segmentsLocked,
      };
    }

    const event = new CustomEvent(Enums.Events.ERROR_EVENT, {
      detail: {
        type: 'Segmentation',
        message: 'Cannot perform brush operation on the selected viewport',
      },
      cancelable: true,
    });
    eventTarget.dispatchEvent(event);

    return null;
  }

  protected createHoverData(element, centerCanvas?) {
    const enabledElement = getEnabledElement(element);
    const { viewport } = enabledElement;

    const camera = getViewportICamera(viewport);
    const { viewPlaneNormal, viewUp } = camera;

    if (!viewPlaneNormal || !viewUp) {
      return;
    }

    const viewportIdsToRender = [viewport.id];

    const activeSegmentationData = this.getActiveSegmentationData(viewport);

    if (!activeSegmentationData) {
      return;
    }

    const { segmentIndex, segmentationId, segmentColor } =
      activeSegmentationData;

    // Center of circle in canvas Coordinates
    const brushCursor = {
      metadata: {
        viewPlaneNormal: <Types.Point3>[...viewPlaneNormal],
        viewUp: <Types.Point3>[...viewUp],
        FrameOfReferenceUID: viewport.getFrameOfReferenceUID(),
        referencedImageId: '',
        toolName: this.getToolName(),
        segmentColor,
      },
      data: {},
    };

    return {
      brushCursor,
      centerCanvas,
      segmentIndex,
      viewport,
      segmentationId,
      segmentColor,
      viewportIdsToRender,
    };
  }

  protected getActiveSegmentationData(viewport) {
    const viewportId = viewport.id;
    const activeRepresentation = getActiveSegmentation(viewportId);

    if (!activeRepresentation) {
      return;
    }

    const { segmentationId } = activeRepresentation;
    const segmentIndex = getActiveSegmentIndex(segmentationId);

    if (!segmentIndex) {
      return;
    }

    const segmentColor = getSegmentIndexColor(
      viewportId,
      segmentationId,
      segmentIndex
    );

    return {
      segmentIndex,
      segmentationId,
      segmentColor,
    };
  }

  protected getOperationData(
    element?
  ): ModifiedLabelmapToolOperationData | undefined {
    const editData = this._editData || this.createEditData(element);
    const hoverData = this._hoverData || this.createHoverData(element);

    if (!editData || !hoverData) {
      return;
    }

    const { segmentIndex, segmentationId, brushCursor } = hoverData;
    const { data, metadata = {} } = brushCursor || {};
    const { viewPlaneNormal, viewUp } = metadata;
    const points = data?.editPoints || data?.handles?.points;

    const configColor =
      this.configuration.preview?.previewColors?.[segmentIndex];
    const { viewport } = getEnabledElement(element) || {};

    if (!viewport || !segmentIndex || !segmentationId) {
      return;
    }

    const segmentColor = getSegmentIndexColor(
      viewport.id,
      segmentationId,
      segmentIndex
    );

    if (!configColor && !segmentColor) {
      return;
    }

    let previewColor = null,
      previewSegmentIndex = null;
    if (this.configuration.preview?.enabled) {
      previewColor = configColor || lightenColor(...segmentColor);
      previewSegmentIndex = 255;
    }

    const operationData = {
      ...editData,
      points,
      segmentIndex,
      viewPlaneNormal,
      previewOnHover: !this._previewData.isDrag,
      toolGroupId: this.toolGroupId,
      segmentationId,
      viewUp,
      centerSegmentIndexInfo: this.centerSegmentIndexInfo,
      activeStrategy: this.configuration.activeStrategy,
      configuration: this.configuration,
      // Provide the preview information so that data can be used directly
      previewColor,
      previewSegmentIndex,
      createMemo: this.createMemo.bind(this),
      hoverData: this._hoverData,
    };
    return operationData;
  }

  /**
   * Adds a preview that can be filled with data.
   */
  public addPreview(
    element = this._previewData.element,
    options?: { acceptReject: boolean }
  ) {
    const { _previewData } = this;
    const acceptReject = options?.acceptReject;
    if (acceptReject === true) {
      this.acceptPreview(element);
    } else if (acceptReject === false) {
      this.rejectPreview(element);
    }
    const enabledElement = getEnabledElement(element);
    const operationData = this.getOperationData(element);

    if (!enabledElement || !operationData) {
      return;
    }

    const results = this.applyActiveStrategyCallback(
      enabledElement,
      operationData,
      StrategyCallbacks.AddPreview
    );
    _previewData.isDrag = true;
    // If the results are modified, we store it as preview data
    if (results?.modified) {
      _previewData.preview = results;
      _previewData.element = element;
    }
    return results;
  }

  /**
   * Cancels any preview view being shown, resetting any segments being shown.
   */
  public rejectPreview(element = this._previewData.element) {
    if (!element) {
      return;
    }
    this.doneEditMemo();
    const enabledElement = getEnabledElement(element);
    const operationData = this.getOperationData(element);

    if (!enabledElement || !operationData) {
      return;
    }

    // A reject only has work to do when a preview exists. `previewData` is shared by
    // every labelmap tool on purpose, so any tool can reject the preview that any other
    // tool created, but the element alone says only that some tool has painted. Every
    // paint path stores its result through `_setPreview`, so `preview` is set when, and
    // only when, preview voxels are on the labelmap. See `BrushTool.rejectPreview` for
    // what running the strategy without a preview costs.
    if (this._previewData.preview) {
      this.applyActiveStrategyCallback(
        enabledElement,
        operationData,
        StrategyCallbacks.RejectPreview
      );
    }

    // Make sure to fully reset all preview related data
    this._previewData.preview = null;
    this._previewData.isDrag = false;
  }

  /**
   * Accepts a preview, marking it as the active segment.
   */
  public acceptPreview(element = this._previewData.element) {
    if (!element) {
      return;
    }

    const operationData = this.getOperationData(element);
    if (!operationData) {
      return;
    }

    // Track the memo ID if it was from an acceptPreview operation
    if (this.memo && this.memo.id) {
      // Store the element and current segment index

      this.acceptedMemoIds.set(this.memo.id, {
        element,
        segmentIndex: operationData.segmentIndex,
      });
    }

    const enabledElement = getEnabledElement(element);

    this.applyActiveStrategyCallback(
      enabledElement,
      operationData,
      StrategyCallbacks.AcceptPreview
    );

    // perform the commit after we accept the preview so that
    // we choose the correct timestamp with the confirmed segment index
    this.doneEditMemo();
    this._previewData.preview = null;
    this._previewData.isDrag = false;
  }

  /**
   * This function converts contours on this view into labelmap data, using the
   * handle[0] state
   *
   * @param viewport - The viewport containing the contours to be converted.
   * @param options - Optional configuration settings.
   * @param options.removeContours - Whether to remove the original contours from the view after conversion. Defaults to `true`.
   * @param options.annotationFilter - A callback function to filter the annotations that should be converted.
   * @returns void

   */
  public static viewportContoursToLabelmap(
    viewport: Types.IViewport,
    options?: {
      removeContours?: boolean;
      annotationFilter?: (annotations: Annotations) => Annotations;
      strokeStartPoint?: Types.Point3;
    }
  ) {
    const removeContours = options?.removeContours ?? true;
    const annotations = getAllAnnotations();
    const polylineAnnotations = annotations.filter(
      (annotation) => annotation.data.contour?.polyline?.length
    );
    const contourAnnotations = options?.annotationFilter
      ? options.annotationFilter(polylineAnnotations)
      : polylineAnnotations;
    if (!contourAnnotations.length) {
      return;
    }
    const viewAnnotations = filterAnnotationsForDisplay(
      viewport,
      contourAnnotations
    );
    if (!viewAnnotations?.length) {
      return;
    }

    const brushInstance = new LabelmapBaseTool(
      {},
      {
        configuration: {
          strategies: {
            FILL_INSIDE_CIRCLE: fillInsideCircle,
          },
          activeStrategy: 'FILL_INSIDE_CIRCLE',
        },
      }
    );
    const preview = brushInstance.addPreview(viewport.element);

    // Use type assertion for the preview object
    const { memo, segmentationId } = preview as {
      memo: LabelmapMemo.LabelmapMemo;
      segmentationId: string;
    };
    const previewVoxels = memo?.voxelManager;
    const segmentationVoxels =
      previewVoxels.sourceVoxelManager || previewVoxels;

    // Create an undo history for the operation
    // Iterate through the canvas space in canvas index coordinates
    const imageData = viewport
      .getDefaultActor()
      .actor.getMapper()
      .getInputData();

    // The geometry that the voxel slab iterator walks. The labelmap is derived
    // from this image data, so the two share every index.
    const volume = {
      dimensions: imageData.getDimensions(),
      direction: imageData.getDirection(),
      spacing: imageData.getSpacing(),
      origin: imageData.getOrigin(),
    };

    const isSegmentEditable = createSegmentEditableLookup(
      viewport.id,
      segmentationId
    );

    for (const annotation of viewAnnotations) {
      const { polyline } = annotation.data.contour;
      const camera = viewport.getCamera();
      const viewPlaneNormal = asUnitNormal(
        annotation.metadata?.viewPlaneNormal ?? camera.viewPlaneNormal
      );

      const activeIndex = getActiveSegmentIndex(segmentationId);
      // The point that the caller gives comes first. `polyline[0]` is not the
      // point where the user starts the stroke, because a boolean operation
      // rebuilds the contour and chooses its own first point, and
      // `updateContourPolyline` can reverse the order of the points. The
      // start point decides between the add and the remove below, so a caller
      // that knows the point of the user must give that point.
      const startPoint =
        options?.strokeStartPoint ||
        annotation.data.handles?.[0] ||
        polyline[0];
      const startIndex = imageData.worldToIndex(startPoint).map(Math.round);
      const startValue = segmentationVoxels.getAtIJKPoint(startIndex) || 0;
      let hasZeroIndex = false;
      let hasPositiveIndex = false;
      for (const polyPoint of polyline) {
        const polyIndex = imageData.worldToIndex(polyPoint).map(Math.round);
        const polyValue = segmentationVoxels.getAtIJKPoint(polyIndex);
        if (polyValue === startValue) {
          hasZeroIndex = true;
        } else if (polyValue >= 0) {
          hasPositiveIndex = true;
        }
      }
      const hasBoth = hasZeroIndex && hasPositiveIndex;
      let segmentIndex = hasBoth
        ? startValue
        : startValue === 0
          ? activeIndex
          : 0;

      // Fall back to the active segment, then drop the annotation.
      if (!isSegmentEditable(segmentIndex)) {
        segmentIndex = activeIndex;
      }

      if (!isSegmentEditable(segmentIndex)) {
        if (removeContours) {
          removeContourAnnotation(annotation);
        }
        continue;
      }

      // Fill the outline the way a brush of the same shape fills it. See
      // `iterateContourFillVoxels` for the shape and the depth rule.
      for (const ijk of iterateContourFillVoxels({
        volume,
        polyline,
        viewPlaneNormal,
        imageData,
      })) {
        const currentSegmentIndex = segmentationVoxels.getAtIJK(
          ijk[0],
          ijk[1],
          ijk[2]
        );
        if (!isSegmentEditable(currentSegmentIndex)) {
          continue;
        }
        previewVoxels.setAtIJK(ijk[0], ijk[1], ijk[2], segmentIndex);
      }

      if (removeContours) {
        removeContourAnnotation(annotation);
      }
    }

    const slices = previewVoxels.getArrayOfModifiedSlices();
    triggerSegmentationDataModified(segmentationId, slices);
    brushInstance.doneEditMemo();
  }
}

const SEGMENT_UNKNOWN = 0;
const SEGMENT_EDITABLE = 1;
const SEGMENT_BLOCKED = 2;

/**
 * Returns a lookup that tells the fill loop if it can write over a segment
 * index. A locked segment and a hidden segment both reject the write.
 *
 * The state is read once per segment index and cached in one Uint8Array that
 * the iteration fills and grows.
 */
function createSegmentEditableLookup(
  viewportId: string,
  segmentationId: string
): (segmentIndex: number) => boolean {
  const segments = getSegmentation(segmentationId)?.segments;
  const hiddenSegmentIndices = getHiddenSegmentIndices(viewportId, {
    segmentationId,
    type: SegmentationRepresentations.Labelmap,
  });

  let cache = new Uint8Array(64);

  return function isSegmentEditable(segmentIndex: number): boolean {
    if (!(segmentIndex > 0)) {
      return true;
    }

    if (segmentIndex >= cache.length) {
      const grown = new Uint8Array(
        Math.max(segmentIndex + 1, cache.length * 2)
      );
      grown.set(cache);
      cache = grown;
    }

    const cached = cache[segmentIndex];
    if (cached !== SEGMENT_UNKNOWN) {
      return cached === SEGMENT_EDITABLE;
    }

    // An undeclared labelmap value has no entry, and so no lock.
    const isEditable =
      segments?.[segmentIndex]?.locked !== true &&
      !hiddenSegmentIndices.has(segmentIndex);

    cache[segmentIndex] = isEditable ? SEGMENT_EDITABLE : SEGMENT_BLOCKED;
    return isEditable;
  };
}

/** Removes a consumed contour from the annotation state and from annotationUIDsMap. */
function removeContourAnnotation(annotation): void {
  removeAnnotation(annotation.annotationUID);

  if ((annotation as ContourSegmentationAnnotation).data?.segmentation) {
    removeContourSegmentationAnnotation(
      annotation as ContourSegmentationAnnotation
    );
  }
}

function lightenColor(r, g, b, a, factor = 0.4) {
  return [
    Math.round(r + (255 - r) * factor),
    Math.round(g + (255 - g) * factor),
    Math.round(b + (255 - b) * factor),
    a,
  ];
}
